import { useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { Check, X, Info, Shield, Pencil, RotateCcw, Loader2, Sparkles } from 'lucide-react';

/**
 * Role Permissions Matrix — editable (Phase 2).
 *
 * Defaults live in this file. The CEO can override any cell; overrides are
 * persisted in the `role_permissions` collection and merged on render.
 */

const ROLES = [
  { id: 'ceo', label: 'CEO', fg: '#7C3AED', bg: '#F3E8FF' },
  { id: 'leader', label: 'Leader', fg: '#1B2A4A', bg: '#EEF2FF' },
  { id: 'sub_leader', label: 'Sub-leader', fg: '#378ADD', bg: '#DBEAFE' },
  { id: 'submitter', label: 'Submitter', fg: '#2D8B5E', bg: '#DCFCE7' },
  { id: 'cost_controller', label: 'Cost Controller', fg: '#BA7517', bg: '#FEF3C7' },
  { id: 'viewer', label: 'Viewer', fg: '#6B7280', bg: '#F3F4F6' },
];

const MATRIX = [
  { key: 'see_all_divs', label: 'See all divisions', group: 'Visibility',
    perms: { ceo: true, leader: false, sub_leader: false, submitter: false, cost_controller: false, viewer: false } },
  { key: 'see_own_div', label: 'See own division', group: 'Visibility',
    perms: { ceo: true, leader: true, sub_leader: true, submitter: true, cost_controller: 'Cost only', viewer: true } },
  { key: 'see_pipeline', label: 'See pipeline deal values', group: 'Visibility',
    perms: { ceo: true, leader: true, sub_leader: 'Own scope', submitter: false, cost_controller: false, viewer: false } },
  { key: 'see_salary', label: 'See salary data', group: 'Visibility',
    perms: { ceo: true, leader: true, sub_leader: false, submitter: false, cost_controller: 'Aggregates only', viewer: false } },

  { key: 'edit_targets', label: 'Edit revenue targets', group: 'Edit',
    perms: { ceo: true, leader: false, sub_leader: false, submitter: false, cost_controller: false, viewer: false } },
  { key: 'enter_daily_sales', label: 'Enter daily sales', group: 'Edit',
    perms: { ceo: true, leader: true, sub_leader: true, submitter: true, cost_controller: false, viewer: false } },
  { key: 'enter_costs', label: 'Enter cost / payables', group: 'Edit',
    perms: { ceo: true, leader: true, sub_leader: false, submitter: false, cost_controller: true, viewer: false } },
  { key: 'update_pipeline', label: 'Update pipeline deals', group: 'Edit',
    perms: { ceo: true, leader: true, sub_leader: 'Own scope', submitter: false, cost_controller: false, viewer: false } },
  { key: 'weekly_update', label: 'Submit weekly updates', group: 'Edit',
    perms: { ceo: true, leader: true, sub_leader: true, submitter: false, cost_controller: true, viewer: false } },

  { key: 'manage_people', label: 'Manage people roster', group: 'Admin',
    perms: { ceo: true, leader: 'Own division', sub_leader: 'Own outlet', submitter: false, cost_controller: false, viewer: false } },
  { key: 'admin_panel', label: 'Access Admin Panel', group: 'Admin',
    perms: { ceo: true, leader: false, sub_leader: false, submitter: false, cost_controller: false, viewer: false } },
  { key: 'assign_sops', label: 'Assign SOPs', group: 'Admin',
    perms: { ceo: true, leader: 'Own division', sub_leader: 'Own outlet', submitter: false, cost_controller: false, viewer: false } },
  { key: 'download_sops', label: 'Download SOP source files', group: 'Admin',
    perms: { ceo: true, leader: 'Own division', sub_leader: false, submitter: false, cost_controller: false, viewer: false } },
  { key: 'view_ip_register', label: 'View IP Register', group: 'Admin',
    perms: { ceo: true, leader: false, sub_leader: false, submitter: false, cost_controller: false, viewer: false } },
];

const defaultValue = (rowKey, roleId) => {
  const row = MATRIX.find(r => r.key === rowKey);
  return row ? row.perms[roleId] : false;
};

function CellDisplay({ value, overridden }) {
  const ring = overridden ? '0 0 0 2px #A78BFA' : 'none';
  const base = { borderRadius: 4, padding: '2px 4px', boxShadow: ring };
  if (value === true) return <span style={base} title={overridden ? 'Custom override: Allowed' : 'Allowed'}><Check size={14} style={{ color: '#166534' }} /></span>;
  if (value === false) return <span style={base} title={overridden ? 'Custom override: Blocked' : 'Blocked'}><X size={14} style={{ color: '#D1D5DB' }} /></span>;
  return <span className="text-[10px] italic" style={{ ...base, color: '#BA7517' }} title={overridden ? `Custom: ${value}` : value}>{value}</span>;
}

function EditPopover({ row, roleId, currentValue, defaultVal, isOverride, onSave, onReset, onClose, saving }) {
  const initialChoice =
    currentValue === true ? 'allow' :
    currentValue === false ? 'block' : 'scope';
  const [choice, setChoice] = useState(initialChoice);
  const [scopeText, setScopeText] = useState(typeof currentValue === 'string' ? currentValue : 'Own scope');

  const submit = () => {
    let val;
    if (choice === 'allow') val = true;
    else if (choice === 'block') val = false;
    else val = (scopeText || '').trim() || 'Own scope';
    onSave(val);
  };

  const roleLabel = ROLES.find(r => r.id === roleId)?.label || roleId;

  return (
    <div
      className="absolute z-50 bg-white rounded-lg shadow-xl border p-3 w-64"
      style={{ borderColor: '#E5E1DC', top: '100%', left: 0, marginTop: 4 }}
      onClick={(e) => e.stopPropagation()}
      data-testid="perm-edit-popover"
    >
      <div className="text-[11px] font-semibold mb-2" style={{ color: '#1B2A4A' }}>
        {roleLabel} — {row.label}
      </div>
      <div className="space-y-1.5 text-xs">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="radio" name={`c-${row.key}-${roleId}`} checked={choice === 'allow'} onChange={() => setChoice('allow')} data-testid="perm-opt-allow" />
          <Check size={12} style={{ color: '#166534' }} /> Allowed
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="radio" name={`c-${row.key}-${roleId}`} checked={choice === 'block'} onChange={() => setChoice('block')} data-testid="perm-opt-block" />
          <X size={12} style={{ color: '#9CA3AF' }} /> Blocked
        </label>
        <label className="flex items-start gap-2 cursor-pointer">
          <input type="radio" name={`c-${row.key}-${roleId}`} checked={choice === 'scope'} onChange={() => setChoice('scope')} className="mt-1" data-testid="perm-opt-scope" />
          <div className="flex-1">
            <div className="italic" style={{ color: '#BA7517' }}>Scoped text</div>
            <input
              type="text"
              value={scopeText}
              onChange={(e) => setScopeText(e.target.value)}
              onFocus={() => setChoice('scope')}
              className="w-full mt-1 px-2 py-1 text-[11px] border rounded"
              style={{ borderColor: '#E5E1DC' }}
              placeholder="e.g. Own division"
              maxLength={40}
              data-testid="perm-scope-input"
            />
          </div>
        </label>
      </div>
      <div className="flex items-center justify-between mt-3 pt-3 border-t" style={{ borderColor: '#F3F4F6' }}>
        <button
          type="button"
          onClick={onReset}
          disabled={!isOverride || saving}
          className="text-[11px] inline-flex items-center gap-1 disabled:opacity-40"
          style={{ color: '#6B7280' }}
          title={`Reset to default (${String(defaultVal)})`}
          data-testid="perm-reset-cell"
        >
          <RotateCcw size={11} /> Reset
        </button>
        <div className="flex items-center gap-2">
          <button type="button" onClick={onClose} className="text-[11px] px-2 py-1 rounded" style={{ color: '#6B7280' }} data-testid="perm-cancel">
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            className="text-[11px] px-3 py-1 rounded font-semibold text-white inline-flex items-center gap-1"
            style={{ backgroundColor: '#7C3AED' }}
            data-testid="perm-save"
          >
            {saving ? <Loader2 size={11} className="animate-spin" /> : null} Save
          </button>
        </div>
      </div>
    </div>
  );
}

export default function RolePermissionsMatrix({ currentUser }) {
  const isCEO = (currentUser?.role || '').toLowerCase() === 'ceo';
  const [overrides, setOverrides] = useState({}); // { "role|key": value }
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { rowKey, roleId }
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');

  const loadOverrides = async () => {
    try {
      const res = await api.get(`/admin/role-permissions`);
      const map = {};
      (res.data || []).forEach(o => { map[`${o.role}|${o.permission_key}`] = o.value; });
      setOverrides(map);
    } catch (e) {
      // Non-CEO might 401; defaults are fine.
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadOverrides(); }, []);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(''), 2200);
    return () => clearTimeout(t);
  }, [toast]);

  const groups = useMemo(() => MATRIX.reduce((acc, row) => {
    (acc[row.group] = acc[row.group] || []).push(row);
    return acc;
  }, {}), []);

  const valueFor = (rowKey, roleId) => {
    const k = `${roleId}|${rowKey}`;
    return k in overrides ? overrides[k] : defaultValue(rowKey, roleId);
  };
  const isOverridden = (rowKey, roleId) => `${roleId}|${rowKey}` in overrides;

  const handleSave = async (row, roleId, newVal) => {
    setSaving(true);
    try {
      await api.put(`/admin/role-permissions`, {
        role: roleId, permission_key: row.key, value: newVal,
      });
      setOverrides(prev => ({ ...prev, [`${roleId}|${row.key}`]: newVal }));
      setToast(`Updated ${row.label} → ${ROLES.find(r => r.id === roleId)?.label}`);
      setEditing(null);
    } catch (e) {
      setToast(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async (row, roleId) => {
    setSaving(true);
    try {
      await api.delete(`/admin/role-permissions`, {
        params: { role: roleId, permission_key: row.key },
      });
      setOverrides(prev => {
        const next = { ...prev };
        delete next[`${roleId}|${row.key}`];
        return next;
      });
      setToast(`Reset ${row.label} → ${ROLES.find(r => r.id === roleId)?.label}`);
      setEditing(null);
    } catch (e) {
      setToast(e.response?.data?.detail || 'Reset failed');
    } finally {
      setSaving(false);
    }
  };

  const handleResetAll = async () => {
    if (!window.confirm('Reset every permission override to defaults? This cannot be undone.')) return;
    setSaving(true);
    try {
      await api.post(`/admin/role-permissions/reset`, {});
      setOverrides({});
      setToast('All overrides cleared — defaults restored');
    } catch (e) {
      setToast(e.response?.data?.detail || 'Reset failed');
    } finally {
      setSaving(false);
    }
  };

  const overrideCount = Object.keys(overrides).length;

  return (
    <div className="space-y-4" data-testid="role-permissions-matrix" onClick={() => setEditing(null)}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#F3E8FF' }}>
          <Shield size={18} style={{ color: '#7C3AED' }} />
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="text-sm font-bold" style={{ color: '#1B2A4A' }}>Role Permissions Matrix</h2>
            <div className="flex items-center gap-2">
              {overrideCount > 0 && (
                <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold" style={{ backgroundColor: '#F3E8FF', color: '#7C3AED' }} data-testid="override-count">
                  {overrideCount} custom override{overrideCount > 1 ? 's' : ''}
                </span>
              )}
              {isCEO && overrideCount > 0 && (
                <button
                  onClick={handleResetAll}
                  disabled={saving}
                  className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded border"
                  style={{ borderColor: '#E5E1DC', color: '#6B7280' }}
                  data-testid="reset-all-overrides"
                >
                  <RotateCcw size={11} /> Reset all
                </button>
              )}
            </div>
          </div>
          <p className="text-xs mt-0.5" style={{ color: '#6B7280' }}>
            Authoritative source for what each role can do across ScaleBoard.
            {isCEO ? ' Click any cell to customise.' : ' Hover any cell for details.'}
          </p>
          {isCEO ? (
            <p className="text-[11px] mt-2 flex items-center gap-1.5 p-2 rounded" style={{ backgroundColor: '#F3E8FF', color: '#5B21B6' }}>
              <Sparkles size={12} /> Editable mode active. Customised cells show a purple outline and count above.
            </p>
          ) : (
            <p className="text-[11px] mt-2 flex items-center gap-1.5 p-2 rounded" style={{ backgroundColor: '#F7F5F2', color: '#6B7280' }}>
              <Info size={12} /> Only the CEO can edit permissions.
            </p>
          )}
        </div>
      </div>

      {toast && (
        <div className="text-[11px] px-3 py-2 rounded" style={{ backgroundColor: '#ECFDF5', color: '#065F46' }} data-testid="perm-toast">
          {toast}
        </div>
      )}

      <div className="bg-white rounded-lg border overflow-visible" style={{ borderColor: '#E5E1DC' }}>
        <div className="overflow-x-auto overflow-y-visible">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ backgroundColor: '#F7F5F2' }}>
                <th className="text-left px-4 py-3 text-xs font-semibold sticky left-0 z-10" style={{ color: '#6B7280', backgroundColor: '#F7F5F2', minWidth: '240px' }}>Permission</th>
                {ROLES.map(r => (
                  <th key={r.id} className="px-4 py-3" data-testid={`col-${r.id}`}>
                    <span className="inline-block text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: r.bg, color: r.fg }}>{r.label}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y" style={{ borderColor: '#F3F4F6' }}>
              {Object.entries(groups).map(([group, rows]) => (
                <>
                  <tr key={`group-${group}`}>
                    <td colSpan={1 + ROLES.length} className="px-4 py-1.5 text-[10px] font-semibold uppercase tracking-wide" style={{ backgroundColor: '#FAF9F7', color: '#9CA3AF' }}>
                      {group}
                    </td>
                  </tr>
                  {rows.map(row => (
                    <tr key={row.key} data-testid={`row-${row.key}`}>
                      <td className="px-4 py-2.5 sticky left-0 z-10 text-xs" style={{ backgroundColor: '#fff', color: '#1B2A4A' }}>{row.label}</td>
                      {ROLES.map(r => {
                        const val = valueFor(row.key, r.id);
                        const ov = isOverridden(row.key, r.id);
                        const isEditing = editing && editing.rowKey === row.key && editing.roleId === r.id;
                        return (
                          <td key={r.id} className="px-4 py-2.5 text-center relative" data-testid={`cell-${row.key}-${r.id}`}>
                            {isCEO ? (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditing(isEditing ? null : { rowKey: row.key, roleId: r.id });
                                }}
                                className="inline-flex items-center gap-1 hover:bg-slate-50 rounded px-1 py-0.5 cursor-pointer group"
                                data-testid={`edit-cell-${row.key}-${r.id}`}
                              >
                                <CellDisplay value={val} overridden={ov} />
                                <Pencil size={9} className="opacity-0 group-hover:opacity-60 transition-opacity" style={{ color: '#7C3AED' }} />
                              </button>
                            ) : (
                              <CellDisplay value={val} overridden={ov} />
                            )}
                            {isEditing && (
                              <EditPopover
                                row={row}
                                roleId={r.id}
                                currentValue={val}
                                defaultVal={defaultValue(row.key, r.id)}
                                isOverride={ov}
                                saving={saving}
                                onSave={(newVal) => handleSave(row, r.id, newVal)}
                                onReset={() => handleReset(row, r.id)}
                                onClose={() => setEditing(null)}
                              />
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 text-[11px]" style={{ color: '#6B7280' }}>
        <span className="flex items-center gap-1.5"><Check size={12} style={{ color: '#166534' }} /> Allowed</span>
        <span className="flex items-center gap-1.5"><X size={12} style={{ color: '#D1D5DB' }} /> Blocked</span>
        <span className="flex items-center gap-1.5"><em className="italic" style={{ color: '#BA7517' }}>Own scope</em> Scoped</span>
        <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded" style={{ boxShadow: '0 0 0 2px #A78BFA' }} /> Custom override</span>
        {loading && <span className="flex items-center gap-1"><Loader2 size={11} className="animate-spin" /> Loading overrides…</span>}
      </div>
    </div>
  );
}
